import { useState, useMemo, useCallback } from "react";

export const useTableSort = (processedData, pagination = { page: 1, limit: 100 }) => {
  // Get URL parameters for preset sorting
  const urlParams = new URLSearchParams(window.location.search);
  const sort = urlParams.get("sort");

  // Determine initial sort configuration
  const getInitialSortConfig = () => {
    // Only apply preset sorting for index sorting (id)
    if (sort === "ascending" || sort === "descending") {
      return { key: "id", direction: sort };
    }
    // Default to descending order for database row id
    return { key: "id", direction: "ascending" };
  };

  // Set initial sort configuration based on URL parameters or default
  const [sortConfig, setSortConfig] = useState(getInitialSortConfig());

  // Handle sort with memoized callback
  const requestSort = useCallback((key) => {
    setSortConfig((prevConfig) => {
      let direction = "ascending";
      if (prevConfig.key === key && prevConfig.direction === "ascending") {
        direction = "descending";
      }

      // If sort config hasn't changed, return the previous config to prevent re-renders
      if (prevConfig.key === key && prevConfig.direction === direction) {
        return prevConfig;
      }

      return { key, direction };
    });
  }, []);

  // Helper function to sort an array of data by the current sort config
  const sortDataArray = useCallback(
    (dataArray) => {
      if (!dataArray || !dataArray.length) return [];

      return [...dataArray].sort((a, b) => {
        // Special case for sorting by database ID (when key is "id")
        if (sortConfig.key === "id") {
          return sortConfig.direction === "ascending" ? a.id - b.id : b.id - a.id;
        }

        const aVal = a.data[sortConfig.key];
        const bVal = b.data[sortConfig.key];

        // Handle null/undefined values
        if (aVal === null || aVal === undefined) return sortConfig.direction === "ascending" ? -1 : 1;
        if (bVal === null || bVal === undefined) return sortConfig.direction === "ascending" ? 1 : -1;

        // Compare based on type
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortConfig.direction === "ascending" ? aVal - bVal : bVal - aVal;
        }

        // Default string comparison
        return sortConfig.direction === "ascending" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
      });
    },
    [sortConfig]
  );

  // Apply sort to all data and add display indices
  const sortedData = useMemo(() => {
    if (!processedData?.processedData) return { processedData: [] };

    // First, create a map of table indices based on DB row IDs (always ascending)
    // This allows us to maintain consistent table IDs regardless of sort order
    const dbRowIdMap = new Map();

    // Create a sorted copy just for generating the index mapping (always sorted by ID ascending)
    const dbIdSortedData = [...processedData.processedData].sort((a, b) => a.id - b.id);

    // Generate sequential table IDs (1, 2, 3...) based on DB row ID order
    dbIdSortedData.forEach((item, index) => {
      dbRowIdMap.set(item.id, index + 1);
    });

    // Sort the regular data according to the current sort configuration
    const sortedProcessedData = sortDataArray(processedData.processedData);

    // Calculate the offset based on current page
    const pageOffset = (pagination.page - 1) * pagination.limit;

    // Add display indices to the sorted data using our mapping with page offset
    const processedDataWithIndices = sortedProcessedData.map((item) => ({
      ...item,
      displayIndex: pageOffset + dbRowIdMap.get(item.id),
    }));

    // If we have outlier data, sort that too
    if (processedData.outlierData && processedData.outlierData.length) {
      const sortedOutlierData = sortDataArray(processedData.outlierData);

      // Add display indices to outliers using the same mapping with page offset
      const outlierDataWithIndices = sortedOutlierData.map((item) => ({
        ...item,
        displayIndex: pageOffset + (dbRowIdMap.get(item.id) || dbIdSortedData.length + 1 + sortedOutlierData.indexOf(item)),
      }));

      // Return both sorted datasets with display indices
      return {
        ...processedData,
        processedData: processedDataWithIndices,
        outlierData: outlierDataWithIndices,
      };
    }

    // Just return the sorted main data with display indices
    return {
      ...processedData,
      processedData: processedDataWithIndices,
    };
  }, [processedData, sortDataArray, pagination]);

  return {
    sortConfig,
    sortedData,
    requestSort,
  };
};
